import axios from "axios";
import { ToastContentProps, toast } from "react-toastify";
import { fadeSlide } from "../services/utils";
import { ReactElement, JSXElementConstructor, ReactNode } from "react";
import {
  isTokenExpiredBeyondThreshold,
  isTokenExpiredWithinThreshold,
  shouldShowWarningInsteadOfExpired,
  cleanupExpiredToken,
  getWarningCountdownSeconds,
} from "../utils/tokenExpiration";
import { broadcastSessionWarning } from "../utils/authBroadcast";
import { triggerSessionExpired } from "../utils/sessionExpiredEvent";
import { refreshTokenUrl } from "../services/constants";
import authStore from "../contexts/authStore";
import { broadcastAuthEvent } from "../utils/authBroadcast";

const base: any = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// Separate axios instance for refresh token calls to avoid circular dependency
const refreshAxios = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processFailedQueue = (newToken: string | null, error?: any) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (newToken) {
      config.headers["Authorization"] = `Bearer ${newToken}`;
      resolve(config);
    } else {
      reject(error || new Error("Token refresh failed"));
    }
  });

  failedQueue = [];
};

/**
 * Proactively refresh token if it's expiring soon
 * @returns Promise<string | null> - Returns new access token if refresh successful, null otherwise
 */
const proactiveTokenRefresh = async (): Promise<string | null> => {
  try {
    // Get the refresh token from authStore (not the access token)
    const currentRefreshToken = authStore.getRefreshToken();
    if (!currentRefreshToken) {
      return null;
    }

    // Use separate axios instance to avoid circular dependency with interceptors
    const response = await refreshAxios.post(refreshTokenUrl, {
      refresh_token: currentRefreshToken,
    });

    const result = response?.data;

    if (result?.access_token && result?.refresh_token) {
      authStore.setTokens(result.access_token, result.refresh_token);

      return result.access_token;
    } else {
      return null;
    }
  } catch (error: any) {
    console.error("❌ Proactive token refresh: Error occurred:", error);

    // Check if refresh token API returned 401 (unauthorized)
    if (error?.response?.status === 401) {
      // Refresh token is invalid/expired - redirect to login
      cleanupExpiredToken();
      window.location.href = "/login";
    }

    return null;
  }
};

base.interceptors.request.use(
  async (config: { headers: { [x: string]: string }; url?: string }) => {
    const token = authStore.getToken() || localStorage.getItem("token");

    // Skip token validation for authentication endpoints
    const isAuthEndpoint =
      config.url &&
      (config.url.includes("/basic-auth/token") ||
        config.url.includes("/basic-auth/signup") ||
        config.url.includes("/basic-auth/refresh-token") ||
        config.url.includes("/basic-auth/send-otp") ||
        config.url.includes("/basic-auth/verify-otp") ||
        config.url.includes("/basic-auth/reset-password"));

    // If no token and not an auth endpoint, block the request and trigger session expired
    if (!token && !isAuthEndpoint) {
      // No token means we can't show warning - directly show expired modal
      broadcastAuthEvent("session-expired");
      cleanupExpiredToken();
      triggerSessionExpired();
      return Promise.reject(
        new Error("No token present for protected request"),
      );
    }

    // Only check token expiration if token exists and it's not an auth endpoint
    if (token && !isAuthEndpoint) {
      // STEP 1: Check if token is expired beyond threshold (session expired)
      if (isTokenExpiredBeyondThreshold(token)) {
        // Check if we should show warning modal first
        if (shouldShowWarningInsteadOfExpired(token)) {
          // Broadcast session warning to all tabs with warning countdown duration
          const warningCountdownSeconds = getWarningCountdownSeconds();
          broadcastSessionWarning(warningCountdownSeconds);
          // Reject the request to prevent API call
          return Promise.reject(
            new Error("Token expiring soon, showing session warning modal"),
          );
        } else {
          // Broadcast to all tabs first
          broadcastAuthEvent("session-expired");
          // CRITICAL: Remove expired token to prevent infinite redirect loop
          cleanupExpiredToken();
          // Trigger global session expired event to show modal
          triggerSessionExpired();
          // Reject the request to prevent API call
          return Promise.reject(
            new Error(
              "Token expired beyond threshold, showing session expired modal",
            ),
          );
        }
      }

      // STEP 2: Check if token is expired but within refresh threshold
      const needsRefresh = isTokenExpiredWithinThreshold(token);

      if (needsRefresh) {
        // If already refreshing, queue this request and wait
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config });
          });
        }

        // Start refresh process - BLOCK the request until this completes
        isRefreshing = true;

        try {
          const newToken = await proactiveTokenRefresh();

          if (newToken) {
            config.headers["Authorization"] = `Bearer ${newToken}`;

            // Process any queued requests with the new token
            processFailedQueue(newToken);

            // Return the config with fresh token - request will proceed
            return config;
          } else {
            // If refresh failed but token is still within threshold, proceed with current token
            if (!isTokenExpiredBeyondThreshold(token)) {
              config.headers["Authorization"] = `Bearer ${token}`;
              processFailedQueue(token);
              return config;
            } else {
              processFailedQueue(
                null,
                new Error("Token refresh failed and token is expired"),
              );
              cleanupExpiredToken();
              authStore.setToken(null);
              triggerSessionExpired();
              return Promise.reject(
                new Error("Token refresh failed and token is expired"),
              );
            }
          }
        } catch (error) {
          // If refresh failed but token is still within threshold, proceed with current token
          if (!isTokenExpiredBeyondThreshold(token)) {
            config.headers["Authorization"] = `Bearer ${token}`;
            processFailedQueue(token);
            return config;
          } else {
            processFailedQueue(null, error);
            cleanupExpiredToken();
            triggerSessionExpired();
            return Promise.reject(error);
          }
        } finally {
          isRefreshing = false;
        }
      } else {
        // Token is valid, proceed normally
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    } else if (token && isAuthEndpoint) {
      // For auth endpoints, only add token if it's a refresh token request
      if (config.url && config.url.includes("/basic-auth/refresh-token")) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      // For login/signup, don't add any token
    }
    return config;
  },
);

base.interceptors.response.use(
  (response: any) => {
    return response;
  },
  async (error: {
    config: any;
    response: {
      status: number;
      data: {
        message:
          | string
          | number
          | boolean
          | ReactElement<any, string | JSXElementConstructor<any>>
          | Iterable<ReactNode>
          | ((props: ToastContentProps<unknown>) => ReactNode)
          | null
          | undefined;
        detail: string;
      };
    };
  }) => {
    const originalRequest = error.config;
    const path = originalRequest.url.replace(/^https?:\/\/[0-9.]+\//, "/");

    if (error?.response?.status === 500) {
      //unable to import useToast from utils, so dismissed directly
      toast.dismiss();
      toast.error(error?.response?.data?.message, {
        transition: fadeSlide,
      });
      if (path === "/config/basic-auth/refresh-token") {
        localStorage.removeItem("token");
        try {
          sessionStorage.setItem("lastVisitedPath", window.location.pathname);
        } catch {}
        window.location.href = "/login";
      }
      return;
    } else if (
      error?.response?.status === 401 &&
      // !originalRequest._retry &&
      error?.response?.data?.detail &&
      (error?.response?.data?.detail === "Invalid token or token has expired" ||
        error?.response?.data?.detail ===
          "Invalid or expired session. Please log in again")
    ) {
      const currentToken = localStorage.getItem("token");

      // If no token, treat as session expired
      if (!currentToken) {
        // No token means we can't show warning - directly show expired modal
        cleanupExpiredToken();
        triggerSessionExpired();
        return Promise.reject(error);
      }

      // Check if token is expired beyond threshold
      if (isTokenExpiredBeyondThreshold(currentToken)) {
        // Check if we should show warning modal first
        if (shouldShowWarningInsteadOfExpired(currentToken)) {
          // Broadcast session warning to all tabs with warning countdown duration
          const warningCountdownSeconds = getWarningCountdownSeconds();
          broadcastSessionWarning(warningCountdownSeconds);
          return Promise.reject(error);
        } else {
          cleanupExpiredToken();
          triggerSessionExpired();
          return Promise.reject(error);
        }
      }

      // Always attempt refresh for 401/403 errors, regardless of token expiration logic
      // This handles cases where server-side token validation differs from client-side

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          // Get the refresh token for the API call
          const currentRefreshToken = authStore.getRefreshToken();
          if (!currentRefreshToken) {
            processFailedQueue(null, error);
            cleanupExpiredToken();
            triggerSessionExpired();
            return Promise.reject(error);
          }

          // Use separate axios instance to avoid circular dependency with interceptors
          const response = await refreshAxios.post(refreshTokenUrl, {
            refresh_token: currentRefreshToken,
          });

          const result = response?.data;

          if (result?.access_token && result?.refresh_token) {
            // Store both tokens using authStore which handles localStorage sync
            authStore.setTokens(result.access_token, result.refresh_token);

            // Process all queued requests with the new access token
            processFailedQueue(result.access_token);
          } else {
            // If refresh fails, treat as expired beyond threshold
            processFailedQueue(null, error);
            cleanupExpiredToken();
            triggerSessionExpired();
            return Promise.reject(error);
          }
        } catch (refreshError: any) {
          // Check if refresh token API returned 401 (unauthorized)
          if (refreshError?.response?.status === 401) {
            // Refresh token is invalid/expired - redirect to login
            processFailedQueue(null, refreshError);
            cleanupExpiredToken();
            // Navigate to login page immediately for 401 refresh token errors
            window.location.href = "/login";
            return Promise.reject(refreshError);
          } else {
            // For other refresh errors, treat as expired beyond threshold
            processFailedQueue(null, error);
            cleanupExpiredToken();
            triggerSessionExpired();
            return Promise.reject(error);
          }
        } finally {
          isRefreshing = false;
        }
      }

      // Queue the original request for retry after token refresh
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      }).then((config) => {
        return base(config);
      });
    } else if (error?.response?.status === 403) {
      // Forbidden: user is authenticated but lacks permission. Do NOT trigger session expired modal or logout.
      // Store the error message for the 403 page to display as toast
      if (error?.response?.data?.message) {
        toast.dismiss();
        toast.error(error.response.data.message, { transition: fadeSlide });
        try {
          sessionStorage.setItem(
            "403ErrorMessage",
            String(error.response.data.message),
          );
        } catch (e) {
          console.warn(
            "Could not store 403 error message in sessionStorage:",
            e,
          );
        }
      }
      // Do not modify auth state; redirect to 403 page which will show the toast
      window.location.href = "/403";
      return Promise.reject(error);
    } else if (error.response.status === 404) {
      if (
        typeof error.response.data.message === "string" &&
        (error.response.data.message.includes("Comparison_research_query") ||
          error.response.data.message.includes("No validation score found") ||
          error.response.data.message.includes("No comparison score found") ||
          error.response.data.message.includes("No run score found") ||
          error.response.data.message.includes("No GRQ score found"))
      ) {
        return;
      } else {
        //unable to import useToast from utils, so dismissed directly
        toast.dismiss();
        toast.error(error.response.data.message, {
          transition: fadeSlide,
        });
      }
    } else if (error.response.status === 422) {
      //unable to import useToast from utils, so dismissed directly
      toast.dismiss();

      let errorMessage = "Validation error: The request could not be processed";

      // Check if error response has a message field
      if (error?.response?.data?.message) {
        errorMessage = String(error.response.data.message);
      }
      // Check if error response has a detail array (common in FastAPI/Pydantic validation errors)
      else if (Array.isArray(error?.response?.data?.detail)) {
        const details = error.response.data.detail;
        if (details.length > 0) {
          // Extract all error messages from the detail array
          const messages = details
            .map((err: any) => {
              const msg = err.msg || err.message;
              if (!msg) return null;

              // If location info exists, format it nicely
              if (Array.isArray(err.loc) && err.loc.length > 0) {
                // Filter out 'body' from location as it's not user-friendly
                const fieldPath = err.loc
                  .filter((part: any) => part !== "body")
                  .join(" → ");

                if (fieldPath) {
                  return `${fieldPath}: ${msg}`;
                }
              }

              return msg;
            })
            .filter(Boolean);

          if (messages.length > 0) {
            errorMessage = messages.join("; ");
          }
        }
      }
      // Check if detail is a string
      else if (typeof error?.response?.data?.detail === "string") {
        errorMessage = error.response.data.detail;
      }

      toast.error(errorMessage, {
        transition: fadeSlide,
      });
      return Promise.reject(error);
    } else if (![200, 201, 206, 413].includes(error?.response?.status)) {
      //unable to import useToast from utils, so dismissed directly
      toast.dismiss();
      toast.error(error?.response?.data?.message, {
        transition: fadeSlide,
      });
      // Propagate the rejection so caller-level catch blocks (e.g.
      // showToast(extractApiErrorMessage(error, ...))) can run and
      // surface the real backend message instead of a fallback.
      return Promise.reject(error);
    }
  },
);

export default base;
